import React from 'react';
import { useSEO } from '../../../hooks/useSEO';
import { SF_CONTENT as C } from './content';
import WhatsAppCTA from '../SoftwareForDistributors/components/WhatsAppCTA';
import SectionFAQ from '../SoftwareForDistributors/components/SectionFAQ';
import SectionDifferentials from '../SoftwareForDistributors/components/SectionDifferentials';
import './SalesForceLanding.scss';

const SalesForceLanding: React.FC = () => {
  useSEO({
    title: C.seo.title,
    description: C.seo.description,
    canonical: '/sistema-para-forca-de-vendas',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: '77Gestão — Sistema para Força de Vendas',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://77gestao.com.br/sistema-para-forca-de-vendas',
      description: C.seo.description,
    },
  });

  return (
    <div className="sales-force-landing">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="lp-hero" aria-labelledby="sf-hero-title">
        <div className="lp-container">
          <span className="lp-kicker">{C.hero.kicker}</span>
          <div className="lp-hero-title-wrapper">
            <h1 className="lp-title" id="sf-hero-title">{C.hero.title}</h1>
            <div className="lp-hero-icon">
              <i className="fa-solid fa-people-arrows" aria-hidden="true" />
            </div>
          </div>
          <p className="lp-subtitle">{C.hero.subtitle}</p>
          <WhatsAppCTA message={C.hero.ctaMessage} id="cta-hero">
            {C.hero.cta}
          </WhatsAppCTA>
          <p className="lp-meta">{C.hero.urgency}</p>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <div className="sf-stats-strip" aria-hidden="true">
        <div className="lp-container">
          {C.statsStrip.map((s, i) => (
            <div className="sf-stat" key={i}>
              <i className={s.icon} />
              <span className="sf-stat__number">{s.number}</span>
              <span className="sf-stat__label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pain ─────────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--pain" aria-labelledby="sf-pain-title">
        <div className="lp-container">
          <h2 id="sf-pain-title">{C.pain.title}</h2>
          <p className="lp-intro">{C.pain.intro}</p>
          <ul className="sf-pain-list">
            {C.pain.items.map((item, i) => (
              <li key={i}>
                <i className={item.icon} aria-hidden="true" />
                {item.text}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Mid CTA ──────────────────────────────────────────────────── */}
      <div className="sf-mid-cta">
        <div className="lp-container">
          <p>{C.midCta.text}</p>
          <WhatsAppCTA message={C.midCta.ctaMessage} id="cta-mid">
            {C.midCta.cta}
          </WhatsAppCTA>
        </div>
      </div>

      {/* ── Solution ─────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--solution" aria-labelledby="sf-solution-title">
        <div className="lp-container">
          <h2 id="sf-solution-title">{C.solution.title}</h2>
          <p className="lp-intro">{C.solution.intro}</p>
          <div className="lp-cards">
            {C.solution.cards.map((card, i) => (
              <article className="lp-card" key={i}>
                <div className="lp-card__icon">
                  <i className={card.icon} aria-hidden="true" />
                </div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Proof ────────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--proof" aria-labelledby="sf-proof-title">
        <div className="lp-container">
          <h2 id="sf-proof-title">{C.proof.title}</h2>
          <p className="lp-intro">{C.proof.intro}</p>

          <div className="sf-proof-stats">
            {C.proof.stats.map((s, i) => (
              <div className="sf-proof-stat" key={i}>
                <span className="sf-proof-stat__number">{s.number}</span>
                <span className="sf-proof-stat__label">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="lp-proof-box">
            {C.proof.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            <p className="lp-proof-highlight">
              <i className="fa-solid fa-check-circle" aria-hidden="true" />
              {C.proof.highlight}
            </p>
          </div>

          <div className="lp-section-cta">
            <WhatsAppCTA message={C.proof.ctaMessage} id="cta-proof">
              {C.proof.cta}
            </WhatsAppCTA>
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--benefits" aria-labelledby="sf-benefits-title">
        <div className="lp-container">
          <h2 id="sf-benefits-title">{C.benefits.title}</h2>
          <div className="lp-benefits-grid">
            {C.benefits.items.map((item, i) => (
              <article key={i}>
                <div className="lp-benefit__icon">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Differentials ────────────────────────────────────────────── */}
      <SectionDifferentials
        {...C.differentials}
        cta={{ text: 'Falar com a equipe', message: C.hero.ctaMessage }}
      />

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <SectionFAQ {...C.faq} />

      {/* ── CTA Final ────────────────────────────────────────────────── */}
      <section className="lp-cta-final" id="schedule-demo" aria-labelledby="sf-cta-final-title">
        <div className="lp-container">
          <h2 id="sf-cta-final-title">{C.ctaFinal.title}</h2>
          <p>{C.ctaFinal.subtitle}</p>
          <WhatsAppCTA message={C.ctaFinal.ctaMessage} className="lp-btn lp-btn--final" id="cta-final">
            {C.ctaFinal.cta}
          </WhatsAppCTA>
        </div>
      </section>
    </div>
  );
};

export default SalesForceLanding;
